/**
 * The not-found piece: the studio's own page for an address it does not
 * have, in its look, with a way back to the home page.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'not-found',
  description: 'The page for an address the studio does not have: a heading, one line and a link to the home page.',
  options: {
    heading: { type: 'string', default: 'Page not found', maxLength: 80, description: 'The page heading.' },
    text: {
      type: 'string',
      default: 'This page may have moved, or the address has a typo.',
      maxLength: 240,
      description: 'The line under the heading; null for none.',
    },
    linkLabel: { type: 'string', default: 'Go to the home page', maxLength: 60, description: 'The words on the link home.' },
  },
  parts: {
    section: { description: 'The whole page section.', required: false },
    heading: { description: 'The page heading.', required: false, role: 'title' },
    text: { description: 'The line under the heading.', required: false },
    home: { description: 'The link to the home page.', required: false },
  },
  actions: {
    goHome: { description: 'Opens the home page.', args: [] },
  },
  events: {
    wentHome: 'A visitor followed the link to the home page.',
  },
});

/** What the not-found piece knows. */
export interface NotFoundState {
  /** The address the visitor asked for (path, query and hash). */
  readonly path: string;
}

/** What a drawing can ask the not-found piece to do. */
export interface NotFoundActions {
  /** Open the home page. */
  readonly goHome: () => void;
}

/** What the not-found piece tells its host. */
export interface NotFoundEvents {
  /** A visitor followed the link home; carries the address they had asked for. */
  readonly wentHome: { readonly path: string };
}
